import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskPriorityDrawerService } from '../../../services/task-priority-drawer.service';
import { TaskPriorityResponse } from '../../../models/task.model';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-task-priorities-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './task-priorities-list.component.html',
})
export class TaskPrioritiesListComponent implements OnInit, OnDestroy {
  items: TaskPriorityResponse[] = [];
  loading = false;
  error = '';

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: TaskCatalogApiService,
    private drawerService: TaskPriorityDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const saved = localStorage.getItem('task-priorities:pageSize');
    if (saved) this.pageSize = +saved;
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    const isActive =
      this.filtroActive === '' ? undefined : this.filtroActive === 'true';
    this.api
      .listPriorities(this.globalPage, this.pageSize, isActive)
      .subscribe({
        next: (page) => {
          let data = [...page.content];
          if (this.filtroSearch.trim()) {
            const term = this.filtroSearch.trim().toLowerCase();
            data = data.filter(
              (i) =>
                i.name.toLowerCase().includes(term) ||
                (i.label || '').toLowerCase().includes(term),
            );
          }
          this.items = data.sort((a, b) => (a.level ?? 999) - (b.level ?? 999));
          this.globalTotalPages = page.totalPages;
          this.globalTotalElements = page.totalElements;
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error cargando prioridades';
          this.loading = false;
        },
      });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem('task-priorities:pageSize', this.pageSize.toString());
    this.globalPage = 0;
    this.cargar();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open();
  }
  editar(item: TaskPriorityResponse): void {
    this.drawerService.open({ priorityId: item.id });
  }

  toggle(item: TaskPriorityResponse): void {
    this.api
      .updatePriority(item.id, {
        name: item.name,
        label: item.label,
        color: item.color,
        level: item.level ?? 3,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.cargar() });
  }

  async eliminar(item: TaskPriorityResponse): Promise<void> {
    if (item.isActive) {
      await this.confirmDialog.alert({
        title: 'Aviso',
        message:
          'No se puede eliminar una prioridad protegida del sistema. Puedes inhabilitarla con el botón de activar/desactivar.',
        variant: 'warning',
      });
      return;
    }
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar prioridad',
      message: `Eliminar la prioridad "${item.label}"? Esta acción es permanente. Si hay tareas usándola, no podrá eliminarse (reasígnalas o inhabilítalo).`,
    });
    if (!ok) return;
    this.api.deletePriority(item.id).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Prioridad de tarea', `Se eliminó "${item.label || item.name}" correctamente.`);
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar',
        });
      },
    });
  }

  levelLabel(level?: number): string {
    switch (level) {
      case 1:
        return 'Urgente';
      case 2:
        return 'Alta';
      case 3:
        return 'Media';
      case 4:
        return 'Baja';
      default:
        return '—';
    }
  }

  trackById = (_: number, item: TaskPriorityResponse) => item.id;
}
